import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import UserService from "@/core/services/UserService";
import PermissionsService from "@/core/services/PermissionsService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import router from "../../router/index";
import { Auth } from "aws-amplify";
import axios from "axios";

export interface User {
  attributes: unknown;
  id: unknown;
  username: unknown;
}

export interface AxiosInterceptor {
  interceptor: number;
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  isAuthenticated: boolean;
  interceptor: AxiosInterceptor;
  refreshCount: number;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();
  interceptor = {} as AxiosInterceptor;
  refreshCount = 1;

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getAuthErrors() {
    return this.errors;
  }

  /**
   * Get current user object
   * @returns AxiosInterceptor
   */
  get getInterceptor(): AxiosInterceptor {
    return this.interceptor;
  }

  @Mutation
  [Mutations.SET_AUTH_ERROR](error) {
    const awsCognitoErrorCodes = {
      InvalidPasswordException: "awsCognitoErrorCodes.InvalidPasswordException",
      ExpiredCodeException: "awsCognitoErrorCodes.ExpiredCodeException",
      LimitExceededException: "awsCognitoErrorCodes.LimitExceededException",
      NotAuthorizedException: "awsCognitoErrorCodes.NotAuthorizedException",
      CodeMismatchException: "awsCognitoErrorCodes.CodeMismatchException",
      InvalidParameterException:
        "awsCognitoErrorCodes.InvalidParameterException",
      PasswordResetRequiredException:
        "awsCognitoErrorCodes.PasswordResetRequiredException",
      UserNotFoundException: "awsCognitoErrorCodes.UserNotFoundException",
      UserNotConfirmedException:
        "awsCognitoErrorCodes.UserNotConfirmedException",
      UsernameExistsException: "awsCognitoErrorCodes.UsernameExistsException",
    };

    if (error.code !== undefined) {
      if (Object.keys(awsCognitoErrorCodes).includes(error.code)) {
        this.errors = {
          error: awsCognitoErrorCodes[error.code],
          code: error.code,
        };
      } else {
        this.errors = { error: "errorCodes.errorMsg", code: "error" };
      }
    } else {
      this.errors = { error: "errorCodes.errorMsg", code: "error" };
    }
  }

  @Mutation
  [Mutations.SET_PERMISSIONS](payload) {
    let permissions = [];
    if (payload && payload["cognito:groups"] !== undefined) {
      permissions = payload["cognito:groups"];
    }
    PermissionsService.savePermissions(permissions);
  }

  @Mutation
  [Mutations.SET_TOKEN](token) {
    this.isAuthenticated = true;
    JwtService.saveToken(token);
    ApiService.setHeader();
  }

  @Mutation
  [Mutations.SET_USER](user) {
    UserService.saveUserInfo(user);
    this.user = user;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    this.interceptor = {} as AxiosInterceptor;
    this.refreshCount = 1;
    JwtService.destroyToken();
    UserService.destroyUserInfo();

    Object.keys(window.localStorage).forEach((item) => {
      if (
        item !== "lang" &&
        item !== "kt_theme_mode_menu" &&
        item !== "kt_theme_mode_value"
      ) {
        window.localStorage.removeItem(item);
      }
    });
  }

  //Axios Interceptor

  @Mutation
  [Mutations.SET_ITERCEPTOR](interceptor) {
    this.interceptor.interceptor = interceptor;
  }

  @Mutation
  [Mutations.ITERCEPTOR_REFRESH_COUNT](count) {
    this.refreshCount = count;
  }

  @Mutation
  [Mutations.ITERCEPTOR_INCREMENT_REFRESH_COUNT]() {
    this.refreshCount += 1;
  }

  @Mutation
  [Mutations.ITERCEPTOR_CLEAR_REFRESH_COUNT]() {
    this.refreshCount = 1;
  }

  @Action
  async [Actions.LOGIN](user) {
    try {
      const response = await Auth.signIn(user);
      this.context.dispatch(Actions.USER_ME);
      const token = (await Auth.currentSession()).getAccessToken();
      this.context.commit(Mutations.SET_PERMISSIONS, token.payload);
      this.context.commit(Mutations.SET_TOKEN, token.getJwtToken());

      this.context.dispatch(Actions.AXIOS_INTERCEPTOR);
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  async [Actions.REGISTER](user) {
    try {
      const response = await Auth.signUp({
        username: user.email,
        password: user.password,
        attributes: {
          email: user.email,
          given_name: user.givenName,
          family_name: user.familyName,
          "custom:company_name": user.companyName,
          zoneinfo: user.zoneinfo,
          locale: user.locale,
        },
      });
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  async [Actions.VERIFY_CODE](params) {
    try {
      const response = await Auth.confirmSignUp(params.email, params.code);
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  async [Actions.FORGOT_PASSWORD](params) {
    try {
      const response = await Auth.forgotPassword(params.email);
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  async [Actions.FORGOT_PASSWORD_SUBMIT](params) {
    try {
      const response = await Auth.forgotPasswordSubmit(
        params.email,
        params.code,
        params.new_password
      );
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }
  @Action
  async [Actions.CHANGE_PASSWORD](data) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const response = await Auth.changePassword(
        user,
        data.oldPassword,
        data.newPassword
      );

      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  async [Actions.UPDATE_USER_ATTRIBUTES](data) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const response = await Auth.updateUserAttributes(user, data);
      this.context.dispatch(Actions.USER_ME);
      return Promise.resolve(response);
    } catch (error) {
      this.context.commit(Mutations.SET_AUTH_ERROR, error);
      return Promise.reject(error);
    }
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.dispatch(Actions.DELETE_AXIOS_INTERCEPTOR);
    this.context.commit(Mutations.PURGE_AUTH);
  }

  @Action
  async [Actions.USER_ME]() {
    try {
      const response = await Auth.currentUserInfo();
      this.context.commit(Mutations.SET_USER, response);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  @Action
  async [Actions.REFRESH_TOKEN]() {
    try {
      const response = await Auth.currentSession();
      const token = response.getAccessToken();
      this.context.commit(Mutations.SET_PERMISSIONS, token.payload);
      this.context.commit(Mutations.SET_TOKEN, token.getJwtToken());
      return Promise.resolve(token);
    } catch (error) {
      this.context.dispatch(Actions.LOGOUT);
      router.push({ name: "sign-in" });
      return Promise.reject(error);
    }
  }

  @Action
  [Actions.AXIOS_INTERCEPTOR]() {
    const iceptor = ApiService.interceptors().response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response.status !== 401) {
          return new Promise((resolve, reject) => {
            reject(error);
          });
        }
        if (this.refreshCount === 3) {
          return new Promise((resolve, reject) => {
            this.context.dispatch(Actions.LOGOUT);
            this.context.commit(Mutations.ITERCEPTOR_CLEAR_REFRESH_COUNT);
            router.push({ name: "sign-in" });
            reject(error);
          });
        }

        if (error.response.status === 401) {
          return this.context
            .dispatch(Actions.REFRESH_TOKEN)
            .then(() => {
              const config = error.config;
              config.headers[
                "Authorization"
              ] = `Bearer ${JwtService.getToken()}`;
              ApiService.setHeader();
              this.context.commit(Mutations.ITERCEPTOR_INCREMENT_REFRESH_COUNT);
              return new Promise((resolve, reject) => {
                ApiService.axios()
                  .request(config)
                  .then((response) => {
                    this.context.commit(
                      Mutations.ITERCEPTOR_CLEAR_REFRESH_COUNT
                    );
                    resolve(response);
                  })
                  .catch((error) => {
                    this.context.commit(
                      Mutations.ITERCEPTOR_CLEAR_REFRESH_COUNT
                    );
                    reject(error);
                  });
              });
            })
            .catch((error) => {
              return new Promise((resolve, reject) => {
                reject(error);
              });
            });
        }
      }
    );
    this.context.commit(Mutations.SET_ITERCEPTOR, iceptor);
  }

  @Action
  [Actions.DELETE_AXIOS_INTERCEPTOR]() {
    if (
      this.interceptor.interceptor !== null ||
      this.interceptor.interceptor !== undefined
    ) {
      axios.interceptors.request.eject(this.interceptor.interceptor);
      this.context.commit(Mutations.SET_ITERCEPTOR, null);
    }
  }
}
